import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeAuth, getUserManager, getKeycloakUserId } from '../config/keycloak';

const AuthContext = createContext(null);

function toAppUser(oidcUser) {
  return {
    id: oidcUser.profile?.sub,
    name: oidcUser.profile?.name || oidcUser.profile?.preferred_username || 'User',
    email: oidcUser.profile?.email || '',
    photoURL: oidcUser.profile?.picture || null,
    keycloakId: oidcUser.profile?.sub,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [keycloakUser, setKeycloakUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    initAuth();

    // Keep React state in sync with oidc-client-ts's OWN background renewal.
    // Without these, a successful silent refresh updates only its internal
    // WebStorageStateStore — this component's `keycloakUser`/`user` state
    // (and therefore getUserIdDiscover()) would keep serving the pre-refresh
    // token until the next full page load, which could itself be stale by
    // then. addUserLoaded fires on every renewal (silent or interactive);
    // addSilentRenewError logs a failed background attempt instead of
    // failing silently with no signal at all.
    const manager = getUserManager();
    const handleUserLoaded = (loadedUser) => {
      setKeycloakUser(loadedUser);
      setUser(toAppUser(loadedUser));
    };
    const handleSilentRenewError = (err) => {
      console.error('Silent renew error:', err);
    };
    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addSilentRenewError(handleSilentRenewError);
    return () => {
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  const initAuth = async () => {
    try {
      initializeAuth();
      const manager = getUserManager();
      const currentUser = await manager.getUser();

      if (currentUser && !currentUser.expired) {
        setKeycloakUser(currentUser);
        setUser(toAppUser(currentUser));
      }
    } catch (err) {
      console.error('Auth init error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async () => {
    const manager = getUserManager();
    await manager.signinRedirect();
  };

  const signOut = async () => {
    const manager = getUserManager();
    setUser(null);
    setKeycloakUser(null);
    await manager.signoutRedirect();
  };

  const handleCallback = async () => {
    try {
      const manager = getUserManager();
      const callbackUser = await manager.signinRedirectCallback();

      if (callbackUser) {
        setKeycloakUser(callbackUser);
        setUser(toAppUser(callbackUser));
      }

      return callbackUser;
    } catch (err) {
      console.error('Callback error:', err);
      setError(err.message);
      throw err;
    }
  };

  const getAccessToken = () => {
    return keycloakUser?.access_token || null;
  };

  const getUserIdDiscover = () => {
    // Returns the Keycloak user ID (sub claim) as user_id_discover
    return getKeycloakUserId(keycloakUser);
  };

  const value = {
    user,
    keycloakUser,
    loading,
    error,
    signIn,
    signOut,
    handleCallback,
    getAccessToken,
    getUserIdDiscover,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
